<template>
  <div
    class="flex justify-center items-center w-full h-full"
    id="layout-wrapper"
    ref="layoutWrapperRef"
  >
    <div id="layout-wrapper-ratio" class="relative" :style="aspectRatioCss">
      <div
        class="absolute top-0 left-0 grid items-center flex-wrap gap-2 w-full h-full grid-flow-col"
        :class="gridLayoutClasses"
      >
        <ClinicianLeftOverlay v-if="!clinicianStreams.length" />

        <template
          :key="`n-${clinicianStream.jitsiUserId}`"
          v-for="(clinicianStream, index) in clinicianStreams"
        >
          <div
            :class="getGridItemClasses(clinicianStream)"
            :style="getGridItemStyle(clinicianStream, index)"
            v-show="!clinicianStream?.screenshareOff"
          >
            <div class="h-0 w-full relative" style="padding-top: 56.25%">
              <div
                class="
                  absolute
                  w-full
                  h-full
                  top-0
                  left-0
                  items-center
                  justify-center
                  flex flex-col
                "
              >
                <!-- Player -->
                <StreamPlayerRemote
                  :jitsiUserId="clinicianStream.jitsiUserId"
                  :showAudio="!isHiddenConsultationStream && !clinicianStream.streamMuted"
                  :showVideo="
                    !isHiddenConsultationStream &&
                    !isRemoteVideoOff(clinicianStream.jitsiUserId) &&
                    !clinicianStream.streamMuted &&
                    !clinicianStream.bandwidthDisabled
                  "
                  :isAudioMuted="
                    sessionStore.isPutOnHold ||
                    sessionStore.isPhasePaused ||
                    clinicianStream.streamMuted
                  "
                  :setVideoAudioRefs="setVideoAudioRefs"
                />
                <!-- Device icon over video -->
                <div
                  class="absolute left-0 top-0"
                  v-if="
                    !isHiddenConsultationStream &&
                    !isRemoteVideoOff(clinicianStream.jitsiUserId) &&
                    !clinicianStream.streamMuted &&
                    !clinicianStream.bandwidthDisabled &&
                    isMedicalDevice(clinicianStream.jitsiUserId)
                  "
                >
                  <div class="flex justify-center">
                    <ViceMonitor
                      v-if="clinicianStream.userId === 'Monitor'"
                      :class="isMinimised(clinicianStream.jitsiUserId) ? 'h-9 w-9' : 'h-20 w-20'"
                    />
                    <ViceOtoscope
                      v-if="clinicianStream.userId === 'Otoscope'"
                      :class="isMinimised(clinicianStream.jitsiUserId) ? 'h-9 w-9' : 'h-24 w-24'"
                    />
                    <ViceCamera
                      v-if="clinicianStream.userId === 'External Camera'"
                      :class="isMinimised(clinicianStream.jitsiUserId) ? 'h-9 w-9' : 'h-24 w-24'"
                    />
                    <ViceDermatoscope
                      v-if="clinicianStream.userId === 'Dermatoscope'"
                      :class="isMinimised(clinicianStream.jitsiUserId) ? 'h-9 w-9' : 'h-24 w-24'"
                    />
                    <ViceExamCam
                      v-if="clinicianStream.userId === 'Exam Camera'"
                      :class="isMinimised(clinicianStream.jitsiUserId) ? 'h-9 w-9' : 'h-24 w-24'"
                    />
                  </div>
                </div>

                <!-- Device icon over video -->
                <div
                  v-if="clinicianStream.userId === 'vuzix'"
                  :class="
                    clinicianStream.streamMuted || clinicianStream.bandwidthDisabled
                      ? 'absolute content-center'
                      : 'absolute left-0 top-0'
                  "
                >
                  <div class="flex justify-center flex-col items-center">
                    <ViceGlasses
                      :class="[
                        clinicianStream.streamMuted || clinicianStream.bandwidthDisabled
                          ? 'h-36 w-36'
                          : 'h-24 w-24',
                      ]"
                    />
                    <!-- Bandwidth disabled indicator -->
                    <div
                      v-if="clinicianStream.bandwidthDisabled"
                      class="text-white text-sm mt-2 bg-yellow-600 px-2 py-1 rounded"
                    >
                      Disabled to save bandwidth
                    </div>
                  </div>
                </div>

                <div
                  class="justify-center items-center h-full w-full rounded-2xl"
                  :class="{
                    flex:
                      clinicianStream.streamMuted ||
                      clinicianStream.bandwidthDisabled ||
                      !(clinicianStream.userId === 'Stethoscope'),
                    'bg-vice-medium-teal': !clinicianStream.bandwidthDisabled,
                    'bg-gray-600': clinicianStream.bandwidthDisabled,
                  }"
                  v-show="
                    isHiddenConsultationStream ||
                    isRemoteVideoOff(clinicianStream.jitsiUserId) ||
                    clinicianStream.streamMuted ||
                    clinicianStream.bandwidthDisabled
                  "
                >
                  <!-- Stethoscope -->
                  <div
                    v-if="clinicianStream.name === DISPLAY_NAME.STETHOSCOPE"
                    class="text-white"
                    :class="{
                      'flex justify-center gap-y-0 flex-col items-center':
                        clinicianStream.bandwidthDisabled,
                    }"
                  >
                    <ViceStethoscope
                      v-if="clinicianStream.userId === 'Stethoscope'"
                      :class="[
                        stetIconSizeCSS(
                          clinicianStream.jitsiUserId,
                          clinicianStream.streamMuted || clinicianStream.bandwidthDisabled,
                        ),
                        {
                          'absolute ':
                            !clinicianStream.streamMuted && !clinicianStream.bandwidthDisabled,
                        },
                      ]"
                    />
                    <StethoscopeAudioVisualizer
                      v-if="
                        clinicianStream.userId === 'Stethoscope' &&
                        !!clinicianStream?.tracks[0]?.stream &&
                        !clinicianStream.bandwidthDisabled &&
                        !clinicianStream.streamMuted
                      "
                      :stream="clinicianStream.tracks[0].stream"
                    />
                    <!-- Bandwidth disabled indicator -->
                    <div
                      v-if="clinicianStream.bandwidthDisabled"
                      class="text-white text-sm mt-2 bg-yellow-600 px-2 py-1 rounded"
                    >
                      Disabled to save bandwidth
                    </div>
                  </div>

                  <!-- Other streams without video -->
                  <div v-else-if="isMedicalDevice(clinicianStream.jitsiUserId)">
                    <div class="flex justify-center flex-col items-center">
                      <ViceMonitor
                        v-if="clinicianStream.userId === 'Monitor'"
                        :class="[
                          iconSizeCSS(
                            clinicianStream.jitsiUserId,
                            clinicianStream.streamMuted || clinicianStream.bandwidthDisabled,
                          ),
                          clinicianStream.bandwidthDisabled ? 'opacity-40' : '',
                        ]"
                      />
                      <ViceOtoscope
                        v-if="clinicianStream.userId === 'Otoscope'"
                        :class="[
                          iconSizeCSS(
                            clinicianStream.jitsiUserId,
                            clinicianStream.streamMuted || clinicianStream.bandwidthDisabled,
                          ),
                          clinicianStream.bandwidthDisabled ? 'opacity-40' : '',
                        ]"
                      />
                      <ViceCamera
                        v-if="clinicianStream.userId === 'External Camera'"
                        :class="[
                          iconSizeCSS(
                            clinicianStream.jitsiUserId,
                            clinicianStream.streamMuted || clinicianStream.bandwidthDisabled,
                          ),
                          clinicianStream.bandwidthDisabled ? 'opacity-40' : '',
                        ]"
                      />
                      <ViceDermatoscope
                        v-if="clinicianStream.userId === 'Dermatoscope'"
                        :class="[
                          iconSizeCSS(
                            clinicianStream.jitsiUserId,
                            clinicianStream.streamMuted || clinicianStream.bandwidthDisabled,
                          ),
                          clinicianStream.bandwidthDisabled ? 'opacity-40' : '',
                        ]"
                      />
                      <ViceExamCam
                        v-if="clinicianStream.userId === 'Exam Camera'"
                        :class="[
                          iconSizeCSS(
                            clinicianStream.jitsiUserId,
                            clinicianStream.streamMuted || clinicianStream.bandwidthDisabled,
                          ),
                          clinicianStream.bandwidthDisabled ? 'opacity-40' : '',
                        ]"
                      />
                      <!-- Bandwidth disabled indicator -->
                      <div
                        v-if="
                          clinicianStream.bandwidthDisabled && clinicianStream.userId !== 'vuzix'
                        "
                        class="text-white text-sm mt-2 bg-yellow-600 px-2 py-1 rounded"
                      >
                        Disabled to save bandwidth
                      </div>
                    </div>
                  </div>
                  <!-- Clinician main stream without video -->
                  <VideoOff
                    v-else
                    :class="{
                      'sm:h-16 sm:w-16 sm:text-xl': shouldBeMinimized(clinicianStream),
                    }"
                    :name="remoteUserProps[clinicianStream.jitsiUserId]['name']"
                    :profile_photo_url="remoteUserProps[clinicianStream.jitsiUserId]['avatar']"
                    :disabledByBandwidth="clinicianStream.bandwidthDisabled"
                  />
                </div>

                <UserLabel
                  :displayName="getLabelName(clinicianStream.jitsiUserId)"
                  :isMutedMicrophone="!clinicianStream.audio || sessionStore.isPutOnHold"
                  :isExternalDevice="isMedicalDevice(clinicianStream.jitsiUserId)"
                  :localStats="clinicianStream.connectionQuality"
                />

                <!--  Layout toggle options  -->
                <div class="absolute top-0 right-0 z-10">
                  <GridItemActionsPanel
                    :small="shouldBeMinimized(clinicianStream)"
                    v-if="clinicianStreams.length > 1"
                    :isMuted="clinicianStream.streamMuted"
                    :streamId="clinicianStream.jitsiUserId"
                    :isFocused="streamsLayoutStore.focusedStreamId === clinicianStream.jitsiUserId"
                    @focusToggled="toggleFocus"
                  />
                </div>
              </div>
            </div>
          </div>
        </template>
      </div>
    </div>
    <SessionPausedNotification
      class="z-20"
      v-if="sessionStore.isPhasePaused && !sessionStore.isPutOnHold"
    />
    <SessionOnHoldOverlay v-if="sessionStore.isPutOnHold" />
    <PoorConnectionCameraToast
      v-if="showPoorConnectionToast && !sessionStore.isClinician"
      @cameraTurnedOff="handleCameraTurnOff"
      @toastDismissed="handleToastDismissed"
    />
  </div>
  <!-- TODO: Other participants audio tracks -->
  <template
    :key="`n-${participantsStream.jitsiUserId}`"
    v-for="participantsStream in participantsStreams"
  >
    <StreamPlayerRemote
      :jitsiUserId="participantsStream.jitsiUserId"
      :showAudio="!isHiddenConsultationStream"
      :isAudioMuted="sessionStore.isPutOnHold"
      :showVideo="false"
      :setVideoAudioRefs="setVideoAudioRefs"
    />
  </template>

  <!-- Local camera preview -->
  <div
    class="group block overflow-hidden z-20 absolute bottom-0 right-2 w-56"
    v-if="!sessionStore.userIsObserver"
  >
    <StreamPlayerLocal
      class="rounded-xl camera"
      :showTitle="true"
      :showControls="false"
      :isMinimizable="true"
      :statsValue="localConnectionQuality"
    />
  </div>

  <!-- Bandwidth Control Modal -->
  <BandwidthControlModal
    :availableStreams="availableStreams"
    :totalBandwidth="totalBandwidth"
    :disabledCount="disabledCount"
  />
</template>

<script>
import { ref, reactive, onUnmounted, onMounted, watch, computed } from 'vue';
import {
  COMMANDS,
  DISPLAY_NAME,
  STETHOSCOPE_EVENTS,
  USER_ROLE,
  CAMERA_TYPES,
  VICE_SESSION_PHASES,
} from '@/Utils/vice-constants';
import StreamPlayerLocal from '@/Components/Session/Grid/StreamPlayerLocal.vue';
import StreamPlayerRemote from '@/Components/Session/Grid/StreamPlayerRemote.vue';
import VideoOff from '@/Components/Session/Grid/VideoOff.vue';
import { SocketioClient } from '@/Services/SocketioClient';
import useTilesLayoutStore from '@/Stores/useTilesLayoutStore';
import GridItemActionsPanel from '@/Components/Session/Grid/GridItemActionsPanel.vue';
import useSessionStore from '@/Stores/useSessionStore';
import useIOTDevicesStore from '@/Stores/useIOTDevicesStore';
import SessionPausedNotification from '@/Components/Session/Overlays/SessionPausedOverlay';
import SessionOnHoldOverlay from '@/Components/Session/Overlays/SessionOnHoldOverlay';
import ClinicianLeftOverlay from '@/Components/Session/Overlays/ClinicianLeftOverlay.vue';
import PoorConnectionCameraToast from '@/Components/Toasts/PoorConnectionCameraToast.vue';
import useConsultationHandlers from '@/Composables/useConsultationHandlers';
import useLayoutComposable from '@/Composables/useLayoutComposable';
import useJitsiTracksComposable from '@/Composables/useJitsiTracksComposable';
import useClinicianStreamsComposable from '@/Composables/useClinicianStreamsComposable';
import useBandwidthControl from '@/Composables/useBandwidthControl';
import useBandwidthControlStore from '@/Stores/useBandwidthControlStore';
import BandwidthControlModal from '@/Components/Modals/BandwidthControlModal.vue';
import JitsiService from '@/Services/JitsiService';
import useLocalStreamingDevicesStore from '@/Stores/useLocalStreamingDevicesStore';
import ViceStethoscope from '@/Components/Icons/ViceStethoscope.vue';
import StethoscopeAudioVisualizer from '@/Components/Session/Grid/StethoscopeAudioVisualizer.vue';
import UserLabel from '@/Components/Session/Grid/UserLabel.vue';
import ViceDermatoscope from '@/Components/Icons/ViceDermatoscope.vue';
import ViceMonitor from '@/Components/Icons/ViceMonitor.vue';
import ViceCamera from '@/Components/Icons/ViceCamera.vue';
import ViceOtoscope from '@/Components/Icons/ViceOtoscope.vue';
import ViceGlasses from '@/Components/Icons/ViceGlasses.vue';
import ViceExamCam from '@/Components/Icons/ViceExamCam.vue';

export default {
  emits: ['show-header'],
  components: {
    ViceExamCam,
    ViceGlasses,
    VideoOff,
    UserLabel,
    ViceCamera,
    ViceMonitor,
    ViceOtoscope,
    ViceStethoscope,
    ViceDermatoscope,
    StreamPlayerLocal,
    StreamPlayerRemote,
    ClinicianLeftOverlay,
    SessionOnHoldOverlay,
    GridItemActionsPanel,
    SessionPausedNotification,
    StethoscopeAudioVisualizer,
    BandwidthControlModal,
    PoorConnectionCameraToast,
  },
  setup(props, { emit }) {
    const sessionStore = useSessionStore();
    const streamsLayoutStore = useTilesLayoutStore();
    const streamingDeviceStore = useIOTDevicesStore();
    const multiCameraDevicesStore = useLocalStreamingDevicesStore();

    const videoAudioRefs = reactive({});
    const remoteUserProps = reactive({});

    const myJitsiId = ref(null);
    const jitsiRoom = ref(null);
    const disconnect = ref(null);
    const pulseVisualizer = ref(null);
    const localConnectionQuality = ref(0);
    const isStethoscopeConnected = ref(false);

    // Poor connection camera toast state
    const showPoorConnectionToast = ref(false);
    const qualityNotificationState = ref({
      hasBeenNotified: false,
      lastQualityAboveThreshold: true,
    });

    const internalCameraUser = computed(
      () => multiCameraDevicesStore.allCameraTypes[CAMERA_TYPES.INTERNAL_WEBCAM],
    );

    const isReady = computed(
      () => jitsiRoom.value && (checkIfAudioAndVideoTracksExist() || sessionStore.userIsObserver),
    );

    const clinicianStreams = computed(() => {
      const filteredStreams = _.filter(
        remoteUserProps,
        (_props) => useStreams.isClinicianName(_props.name) || _props.name === DISPLAY_NAME.VUZIX,
      );
      if (isStethoscopeConnected?.value) {
        filteredStreams.push({
          name: DISPLAY_NAME.STETHOSCOPE,
          role: USER_ROLE.PARTICIPANT,
          userId: 'stethoscopeId',
          jitsiUserId: 'stethoscopeId',
          tracks: [],
          audio: true,
          video: false,
          videoType: '',
        });
      }
      return filteredStreams;
    });

    /**
     * When a user joins a conference, add the media track to the conference/room
     */
    const onConferenceJoined = async () => {
      useActions.isJoined.value = true;
      const localTracks = internalCameraUser.value?.localTracks;
      const conferenceAudioTrack = internalCameraUser.value.jitsiRoom.getLocalAudioTrack();
      if (localTracks?.length) {
        for (const newTrack of localTracks) {
          // Replace audio track if already exists
          if (conferenceAudioTrack?.type === newTrack?.type || conferenceAudioTrack === newTrack) {
            // eslint-disable-next-line no-await-in-loop
            await internalCameraUser.value.jitsiRoom?.replaceTrack(conferenceAudioTrack, newTrack);
          } else {
            // eslint-disable-next-line no-await-in-loop
            await internalCameraUser.value.jitsiRoom?.addTrack(newTrack);
          }
        }
      }
    };

    const useActions = useJitsiTracksComposable(props, {
      myJitsiId,
      jitsiRoom,
      videoAudioRefs,
      internalCameraUser,
    });

    const { events, commands, enableStream, disableStream } = useConsultationHandlers({
      remoteUserProps,
      videoAudioRefs,
      onConferenceJoined,
      myJitsiId,
      resetMicState: useActions.resetMicState,
      room: jitsiRoom,
      localConnectionQuality,
    });

    const useStyles = useLayoutComposable({
      clinicianStreams,
      streamsLayoutStore,
    });

    const setVideoAudioRefs = useActions.setVideoAudioRefs;

    const useStreams = useClinicianStreamsComposable({ remoteUserProps });

    // Bandwidth control setup
    const bandwidthStore = useBandwidthControlStore();
    const streamHandlers = {
      enableStream,
      disableStream,
    };
    const { availableStreams, totalBandwidth, disabledCount } = useBandwidthControl(
      remoteUserProps,
      streamHandlers,
    );

    const checkIfAudioAndVideoTracksExist = () => {
      const trackTypes = [];
      multiCameraDevicesStore.checkPermissions().then((permitted) => {
        if (!permitted.camera || !permitted.microphone) {
          console.log('Not enough permissions. Check them now...');
          sessionStore.checkForPermissions();
        }
      });

      internalCameraUser.value?.localTracks?.forEach((track) => {
        trackTypes.push(track.type);
        if (track.stream?.active === false && sessionStore.isMyCameraOn) {
          track.unmute();
        }
      });

      return trackTypes.includes('audio') && trackTypes.includes('video');
    };

    watch(isReady, (isReadyToJoin) => {
      if (isReadyToJoin) {
        useActions.initializeRoom();
      }
    });

    watch(
      () => sessionStore.isPutOnHold,
      (isOnHold) => {
        jitsiRoom.value?.sendCommandOnce(COMMANDS.PUT_ON_HOLD, {
          value: COMMANDS.PUT_ON_HOLD,
          attributes: {
            isOnHold,
            userRoomId: myJitsiId.value,
          },
          children: [],
        });
      },
    );

    // Watch for poor connection quality
    watch(localConnectionQuality, (newQuality) => {
      // Only for students during consultation phase
      if (
        sessionStore.isClinician ||
        sessionStore.currentPhase !== VICE_SESSION_PHASES.CONSULTATION
      ) {
        return;
      }

      // Quality dropped below 20
      if (newQuality <= 20) {
        if (qualityNotificationState.value.lastQualityAboveThreshold) {
          showPoorConnectionToast.value = true;
          qualityNotificationState.value.hasBeenNotified = true;
          qualityNotificationState.value.lastQualityAboveThreshold = false;
        }
      }

      // Quality recovered above 40 - reset for next drop
      if (newQuality >= 40 && sessionStore.cameraOffDueToQuality) {
        // If previously turned off due to quality, turn it back on
        handleCameraTurnOff(false);

        qualityNotificationState.value.lastQualityAboveThreshold = true;
        qualityNotificationState.value.hasBeenNotified = false;

        // Auto-dismiss toast
        if (showPoorConnectionToast.value) {
          setTimeout(() => {
            showPoorConnectionToast.value = false;
          }, 1000);
        }
      }

      // Auto-dismiss if quality improves to 30+
      if (newQuality >= 30 && showPoorConnectionToast.value) {
        setTimeout(() => {
          showPoorConnectionToast.value = false;
        }, 5000);
      }
    });

    // Watch for phase changes
    watch(
      () => sessionStore.currentPhase,
      (newPhase) => {
        if (newPhase !== VICE_SESSION_PHASES.CONSULTATION) {
          showPoorConnectionToast.value = false;
          sessionStore.setCameraOffDueToQuality(false);
        }
      },
    );

    onMounted(async () => {
      emit('show-header', true);

      const { room, disconnect: disconnectJitsi } = await JitsiService().makeConnection(
        sessionStore.sessionId,
        events,
        commands,
      );
      jitsiRoom.value = room;
      disconnect.value = disconnectJitsi;
      multiCameraDevicesStore.setActiveJitsiRoom(CAMERA_TYPES.INTERNAL_WEBCAM, room);

      // hide header after 10 seconds
      setTimeout(() => {
        emit('show-header', false);
      }, 10000);

      // Todo: init only when stetoscope is connected
      await initializeStethoscopeSocketIO();

      // Initialize bandwidth control for session
      if (!sessionStore.isClinician) {
        const streamIds = Object.keys(remoteUserProps);
        bandwidthStore.initializeSession(sessionStore.sessionId, streamIds);
      }
    });

    onUnmounted(async () => {
      try {
        await disconnect.value();
      } catch (error) {
        console.error(error);
      }
    });

    const initializeStethoscopeSocketIO = async () => {
      streamingDeviceStore.iotSockets.stethoscope = await SocketioClient(sessionStore.sessionId);

      streamingDeviceStore.iotSockets.stethoscope.on(STETHOSCOPE_EVENTS.SIGNAL, ({ signal }) => {
        isStethoscopeConnected.value = true;
        if (!pulseVisualizer.value) return;
        pulseVisualizer.value[0]?.feedPcmPlayer(signal);
      });

      streamingDeviceStore.iotSockets.stethoscope.on(STETHOSCOPE_EVENTS.DEVICE_DISCONNECTED, () => {
        isStethoscopeConnected.value = false;
      });
    };

    const participantsStreams = computed(() =>
      _.filter(
        remoteUserProps,
        (_props) =>
          !useStreams.isClinicianName(_props.name) &&
          _props.name !== DISPLAY_NAME.VUZIX &&
          _props.name !== DISPLAY_NAME.NAVIGATION_USER,
      ),
    );

    const getLabelName = (jitsiId) => {
      const userId = remoteUserProps[jitsiId].userId;
      if (userId === sessionStore.clinicianId.toString()) {
        return sessionStore.sessionDetails.details.clinician.name;
      }
      return remoteUserProps[jitsiId].userId === 'vuzix'
        ? 'Smartglasses'
        : remoteUserProps[jitsiId].userId;
    };

    const isMedicalDevice = (jitsiId) =>
      remoteUserProps[jitsiId].userId !== sessionStore.clinicianId.toString();

    const iconSizeCSS = (jitsiId, isMuted) => {
      if (streamsLayoutStore.focusMode) {
        if (isMuted) {
          return jitsiId === streamsLayoutStore.focusedStreamId ? 'h-64 w-64' : 'h-24 w-24';
        }

        return jitsiId === streamsLayoutStore.focusedStreamId ? 'h-24 w-24' : 'h-9 w-9';
      }

      return isMuted ? 'h-36 w-36' : 'h-24 w-24';
    };

    const stetIconSizeCSS = (jitsiId, isMuted) => {
      if (streamsLayoutStore.focusMode) {
        if (isMuted) {
          return jitsiId === streamsLayoutStore.focusedStreamId ? 'h-64 w-64' : 'h-24 w-24';
        }

        return jitsiId === streamsLayoutStore.focusedStreamId ? 'h-24 w-24' : 'h-9 w-9';
      }

      return isMuted ? 'h-36 w-36' : 'h-24 w-24';
    };

    const isMinimised = (jitsiId) =>
      streamsLayoutStore.focusMode && jitsiId !== streamsLayoutStore.focusedStreamId;

    // Poor connection toast handlers
    const handleCameraTurnOff = async (shouldBeOff) => {
      sessionStore.setCameraOffDueToQuality(shouldBeOff);
      if (internalCameraUser.value.isCameraOn === shouldBeOff) {
        await multiCameraDevicesStore.toggleCamera(CAMERA_TYPES.INTERNAL_WEBCAM);
      }
    };

    const handleToastDismissed = () => {
      showPoorConnectionToast.value = false;
    };

    return {
      DISPLAY_NAME,
      isMinimised,
      iconSizeCSS,
      getLabelName,
      stetIconSizeCSS,
      isMedicalDevice,
      handleCameraTurnOff,
      handleToastDismissed,
      ...useStyles,
      ...useStreams,
      sessionStore,
      disabledCount,
      bandwidthStore,
      totalBandwidth,
      remoteUserProps,
      pulseVisualizer,
      clinicianStreams,
      availableStreams,
      setVideoAudioRefs,
      streamsLayoutStore,
      participantsStreams,
      localConnectionQuality,
      showPoorConnectionToast,
    };
  },
};
</script>
<style lang="scss" scoped></style>
